import React, { useState } from 'react';
import { Home, TrendingUp, Lightbulb, Settings, ChevronRight, ArrowLeft, LogOut, CalendarDays } from 'lucide-react';
import { DjayAvatar } from '../../mascot';
import { Select } from '../../components/ui/Select';
import type { LearnerAccount } from '../../accounts';
export type ParentV2Route = 'home' | 'voortgang' | 'tips' | 'activiteit' | 'settings';
interface NavItem {
  id: ParentV2Route;
  label: string;
  icon: React.ReactNode;
}
const NAV_ITEMS: NavItem[] = [{
  id: 'home',
  label: 'Home',
  icon: <Home className="w-5 h-5" data-id="element-6303" />
}, {
  id: 'voortgang',
  label: 'Voortgang',
  icon: <TrendingUp className="w-5 h-5" data-id="element-6304" />
}, {
  id: 'tips',
  label: 'Tips',
  icon: <Lightbulb className="w-5 h-5" data-id="element-6305" />
}, {
  id: 'activiteit',
  label: 'Activiteit',
  icon: <CalendarDays className="w-5 h-5" data-id="element-6306" />
}];
interface ParentSidebarV2Props {
  activeRoute: ParentV2Route;
  onNavigate: (route: ParentV2Route) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
  parentName?: string;
  linkedLearners?: LearnerAccount[];
  selectedChildId?: string | null;
  onSelectChild?: (id: string) => void;
}
export function ParentSidebarV2({
  activeRoute,
  onNavigate,
  onLogout,
  onReturnHome,
  parentName = 'Ouder',
  linkedLearners = [],
  selectedChildId,
  onSelectChild
}: ParentSidebarV2Props) {
  const [collapsed, setCollapsed] = useState(false);
  const showChildSelector = linkedLearners.length > 1 && onSelectChild;
  return <aside className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0 h-full ${collapsed ? 'w-20' : 'w-64'}`} data-id="element-6307">
      {/* Header / Logo */}
      <div className={`flex items-center border-b border-slate-100 flex-shrink-0 h-20 ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`} data-id="element-6308">
        {!collapsed && <div className="flex items-center gap-3" data-id="element-6309">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm overflow-hidden" data-id="element-6310">
              <DjayAvatar size="xs" emotion="happy" data-id="element-6311" />
            </div>
            <div className="flex flex-col" data-id="element-6312">
              <span className="text-sm font-black text-slate-900 leading-tight tracking-tight" data-id="element-6313">
                Meester djay
              </span>
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-6314">
                Ouderportaal
              </span>
            </div>
          </div>}
        {collapsed && <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm overflow-hidden" data-id="element-6315">
            <DjayAvatar size="xs" emotion="happy" data-id="element-6316" />
          </div>}
        {!collapsed && <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" aria-label="Inklappen" data-id="element-6317">
            <ChevronRight className="w-4 h-4 rotate-180" data-id="element-6318" />
          </button>}
      </div>

      {/* Parent Info */}
      {!collapsed && <div className="px-6 py-4 border-b border-slate-100" data-id="element-6319">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-6320">
            Ingelogd als
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" data-id="element-6321">
            {parentName}
          </div>
        </div>}

      {/* Child Selector */}
      {showChildSelector && !collapsed && <div className="px-4 py-3 border-b border-slate-100" data-id="element-6322">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block" data-id="element-6323">
            Bekijk kind
          </label>
          <div className="relative" data-id="element-6324">
            <Select value={selectedChildId || ''} onChange={e => onSelectChild!(e.target.value)} options={linkedLearners.map(learner => ({
          value: learner.id,
          label: learner.displayName
        }))} className="bg-emerald-50 border-emerald-200 text-slate-900 font-bold hover:bg-emerald-100 focus:ring-emerald-500 focus:border-emerald-300" data-id="element-6325" />
          </div>
        </div>}
      {showChildSelector && collapsed && <div className="px-2 py-3 border-b border-slate-100 flex justify-center" data-id="element-6326">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm border border-emerald-200 cursor-pointer" title={linkedLearners.find(l => l.id === selectedChildId)?.displayName || 'Kind'} data-id="element-6327">
            {(linkedLearners.find(l => l.id === selectedChildId)?.displayName || '?').charAt(0).toUpperCase()}
          </div>
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-3" data-id="element-6328">
        <div className="space-y-1.5" data-id="element-6329">
          {NAV_ITEMS.map(item => {
          const isActive = activeRoute === item.id;
          return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 relative focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} ${isActive ? 'bg-emerald-50 text-emerald-700 font-bold shadow-sm border border-emerald-100/50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium border border-transparent'}`} data-id="element-6330">
                <span className={`flex-shrink-0 transition-colors ${isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-6331">
                  {item.icon}
                </span>
                {!collapsed && <span className="text-sm truncate" data-id="element-6332">{item.label}</span>}
              </button>;
        })}
        </div>
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-slate-100 ${collapsed ? 'p-3' : 'p-5'} space-y-2`} data-id="element-6333">
        {collapsed && <button onClick={() => setCollapsed(false)} className="w-full flex items-center justify-center p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors mb-2" aria-label="Uitklappen" data-id="element-6334">
            <ChevronRight className="w-5 h-5" data-id="element-6335" />
          </button>}

        <button onClick={() => onNavigate('settings')} title={collapsed ? 'Instellingen' : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5'} ${activeRoute === 'settings' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium'}`} data-id="element-6336">
          <Settings className={`w-5 h-5 ${activeRoute === 'settings' ? 'text-emerald-600' : ''}`} data-id="element-6337" />
          {!collapsed && <span className="text-sm" data-id="element-6338">Instellingen</span>}
        </button>

        {onLogout && <button onClick={onLogout} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Uitloggen' : undefined} data-id="element-6339">
            <LogOut className="w-5 h-5" data-id="element-6340" />
            {!collapsed && <span className="text-sm" data-id="element-6341">Uitloggen</span>}
          </button>}

        {onReturnHome && <button onClick={onReturnHome} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Naar leerlingomgeving' : undefined} data-id="element-6342">
            <ArrowLeft className="w-5 h-5" data-id="element-6343" />
            {!collapsed && <span className="text-sm" data-id="element-6344">Leerlingomgeving</span>}
          </button>}
      </div>
    </aside>;
}